import React, { useCallback, useMemo } from 'react';
import { moment, maxMomentDate } from 'utils/momentExtensions';
import FormSelect from 'shared/components/selects/FormSelect';
import { useSelector } from 'react-redux';
import { IPayPeriod } from 'store/entities/timesheet/models/PayPeriod';
import { IFormFieldProps } from '../formFields/models';
import { selectIsJobNumbersLoading, selectJobNumbersById } from 'store/entities/configuration/configurationSelectors';
import { IFormSelect } from './model';
import { separateLogicDecorator } from 'shared/utils/separateLogicDecorator';
import { IJobNumber } from 'shared/models/JobNumber';

interface IJobNumbersProps extends IFormFieldProps, IFormSelect {
    userId?: string;
    payPeriod?: IPayPeriod;
    getText?: (item: IJobNumber) => string;
    filter?: (item: IJobNumber) => boolean;
}

interface IJobNumbersStoreProps {
    options: IJobNumber[];
    isLoading: boolean;
}

export function JobNumbersPure({
    options,
    disabled,
    getText,
    ...props
}: IJobNumbersProps & IJobNumbersStoreProps) {

    const sortedOptions = useMemo(() => {
        return options.sort((a, b) => moment(a).diff(moment(b)));
    }, [options]);

    const defaultGetText = useCallback(({ job_number }: IJobNumber) => {
        return job_number;
    }, []);

    const getTextToUse = getText || defaultGetText;
    return (
        <FormSelect
            {...props}
            getKey={({ id }) => id}
            getText={getTextToUse}
            options={sortedOptions}
            disabled={disabled || !options.length}
        />
    );
}

export const useJobNumbersForUser = (userId?: string, payPeriod?: IPayPeriod) => {
    const jobNumbers = useSelector(selectJobNumbersById);
    return useMemo(() => {
        let filteredJobNumbers = Object.values(jobNumbers);
        if (userId) {
            filteredJobNumbers = filteredJobNumbers.filter(jobNumber => jobNumber.user_id === userId);
        }
        if (payPeriod) {
            const momentPayPeriod = moment.range(moment(payPeriod?.period_start), moment(payPeriod?.period_end).endOf('day'));
            filteredJobNumbers = filteredJobNumbers.filter(
                jobNumber => {
                    const jobNumberEnd = jobNumber.end_date ? moment(jobNumber.end_date).endOf('day') : maxMomentDate;
                    const jobNumberPeriod = moment.range(moment(jobNumber.start_date), jobNumberEnd);
                    return jobNumberPeriod.intersect(momentPayPeriod);
                },
            );
        }
        return filteredJobNumbers;
    }, [userId, jobNumbers, payPeriod]);
};

export const JobNumbers = separateLogicDecorator<
IJobNumbersProps,
IJobNumbersStoreProps
>(({ userId, payPeriod, getText, filter }) => {
    const jobNumbersForUser = useJobNumbersForUser(userId, payPeriod);
    return {
        options: jobNumbersForUser,
        isLoading: useSelector(selectIsJobNumbersLoading),
        getText,
        filter,
    };
})(JobNumbersPure);
